import type { ExcitingWritingIdeaSnapshotProps } from '@/app/my-portfolio/my-portfolio'
import { apiClient } from '@/lib/api-client'
import type { LongWriteAnswerCreateUpdate } from '@wl/api-client'

interface GetActivityResultsProps {
  studentId: string
}

export async function getStudentResults(
  options: GetActivityResultsProps,
  init?: RequestInit,
) {
  const { data } =
    await apiClient.longWritesApi.apiV1LongwritesStudentsStudentIdResultsGet(
      { ...options },
      init,
    )

  if (!data) {
    throw new Error('Failed to fetch student results: no data returned')
  }

  return data
}

interface GetTopicIdeasListProps {
  gradePosition: number
}

export async function getTopicIdeasList(
  options: GetTopicIdeasListProps,
  init?: RequestInit,
) {
  const { items } = await apiClient.longWritesApi.apiV1LongwritesCatalogueGet(
    { ...options },
    init,
  )

  if (!items) {
    throw new Error('Failed to fetch topic ideas list: no items returned')
  }

  return items
}

interface GetLongWritesActivityProps {
  activityId: string
}

export async function getLongWritesActivity(
  options: GetLongWritesActivityProps,
  init?: RequestInit,
) {
  return await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdGet(
    { ...options },
    init,
  )
}

export interface GetLongWritesActivityResultProps {
  activityId: string
  activityResultId: string
}

export async function getLongWritesActivityResult(
  options: GetLongWritesActivityResultProps,
  init?: RequestInit,
) {
  return await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdResultsActivityResultIdGet(
    { ...options },
    { ...init, cache: 'no-store' }, // Ensure no caching for the latest result
  )
}

export interface PostLongWritesActivityResultProps {
  activityId: string
  activityResultId: string
  eventId: string
  userTitle?: string
  userTargetWordCount?: number
  assignmentTaskId?: number
  assignmentId?: string
}

export async function postLongWritesActivityResult(
  options: PostLongWritesActivityResultProps,
  init?: RequestInit,
) {
  const {
    activityId,
    activityResultId,
    eventId,
    userTitle,
    assignmentId,
    assignmentTaskId,
    userTargetWordCount,
  } = options
  await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdResultsActivityResultIdPost(
    {
      activityId,
      activityResultId,
      longWriteResultCreate: {
        userTargetWordCount: userTargetWordCount ?? null,
        eventId,
        userTitle: userTitle ?? '',
        assignmentTaskId: assignmentTaskId ?? 0,
        assignmentId: assignmentId ?? null,
      },
    },
    init,
  )
}

export async function putLongWritesActivityResult(
  options: PostLongWritesActivityResultProps,
  init?: RequestInit,
) {
  const { activityId, activityResultId, eventId, userTargetWordCount } = options
  await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdResultsActivityResultIdPut(
    {
      activityId,
      activityResultId,
      longWriteResultUpdate: {
        eventId,
        userTargetWordCount: userTargetWordCount ?? 0,
      },
    },
    init,
  )
}

interface PutLongWritesActivityResultAnswerProps {
  activityId: string
  activityResultId: string
  activityAnswerId: string
  longWriteAnswerCreateUpdate: LongWriteAnswerCreateUpdate
}

export const putLongWritesActivityResultAnswer = async (
  payload: PutLongWritesActivityResultAnswerProps,
  init?: RequestInit,
) => {
  await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdResultsActivityResultIdAnswersActivityAnswerIdPut(
    payload,
    init,
  )
}

interface DeleteActivityResultProps {
  activityId: string
  activityResultId: string
  longWriteResultDelete?: {
    eventId: string
  }
}

export async function deleteActivityResult(
  options: DeleteActivityResultProps,
  init?: RequestInit,
) {
  await apiClient.longWritesApi.apiV1LongwritesActivitiesActivityIdResultsActivityResultIdDelete(
    { ...options },
    init,
  )
}

export async function getSubmittedLongWritesSnapshots(
  studentId: string,
  init?: RequestInit,
) {
  const { data } =
    await apiClient.excitingWritingIdeasApi.apiV1EwiStudentIdSubmittedSnapshotsGet(
      { studentId, template: 'long-write' },
      init,
    )

  const feedback: ExcitingWritingIdeaSnapshotProps[] = []

  for (const snapshot of data) {
    feedback.push({
      activityId: snapshot.activityId,
      activityResultSnapshotId: snapshot.activityResultSnapshotId,
      activityTitle: snapshot.activityTitle,
      hasFeedback: Boolean(snapshot.feedbackId),
      feedbackMark: snapshot.feedbackMark ?? undefined,
      resultId: snapshot.resultId,
      score: snapshot.score,
      maxPoints: snapshot.maxPoints,
      updatedDate: snapshot.updatedDate,
      text: snapshot.plainText ?? '',
    })
  }

  return feedback.sort(
    (a, b) => b.updatedDate.valueOf() - a.updatedDate.valueOf(),
  )
}

export async function getLongWritesAssignment(
  assignmentId: string,
  init?: RequestInit,
) {
  return await apiClient.longWritesApi.apiV1LongwritesActivitiesResultsAssignmentsAssignmentIdGet(
    { assignmentId },
    { ...init, cache: 'no-store' },
  )
}
